import React, { useState } from 'react';
import { PageHeader, useReveal, GlowDivider } from '../components/Shared';

const REASONS = [
  { value:'collaboration', label:'Research Collaboration' },
  { value:'partnership',   label:'Industry Partnership' },
  { value:'press',         label:'Press & Media' },
  { value:'careers',       label:'Careers & Opportunities' },
  { value:'other',         label:'Other Inquiry' },
];

export default function Contact() {
  useReveal();
  const [form, setForm]       = useState({ name:'', email:'', org:'', reason:'collaboration', message:'' });
  const [submitted, setSubmit] = useState(false);
  const [loading, setLoading]  = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = e => {
    e.preventDefault();
    setLoading(true);
    // Simulate sending (no backend needed — replace with fetch('/api/contact',...) if you add Node)
    setTimeout(() => { setLoading(false); setSubmit(true); }, 1500);
  };

  const inputStyle = {
    width:'100%', background:'rgba(255,255,255,0.04)', border:'1px solid var(--border)',
    borderRadius:10, padding:'13px 18px', color:'var(--text-1)',
    fontFamily:'var(--font-body)', fontSize:'0.88rem', outline:'none',
    transition:'border-color .3s ease, box-shadow .3s ease',
  };
  const labelStyle = { display:'block', fontFamily:'var(--font-mono)', fontSize:'0.65rem', letterSpacing:'2px', textTransform:'uppercase', color:'var(--text-3)', marginBottom:8 };

  const onFocus = e => { e.currentTarget.style.borderColor='rgba(0,255,255,0.35)'; e.currentTarget.style.boxShadow='0 0 20px rgba(0,255,255,0.07)'; };
  const onBlur  = e => { e.currentTarget.style.borderColor='var(--border)'; e.currentTarget.style.boxShadow='none'; };

  return (
    <>
      <PageHeader
        tag="Contact"
        title="Let's"
        accent="Connect"
        sub="Whether you're exploring a collaboration, partnership, or just curious about our work — we'd love to hear from you."
      />

      <GlowDivider />

      <section className="section">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Info column */}
            <div className="lg:col-span-2 flex flex-col gap-6">
              {/* Info cards */}
              {[
                { icon:'📍', title:'Location',  body:'Advanced Materials Research Center\nUniversity District, Building 7\nBoston, MA 02139' },
                { icon:'✉️', title:'Email',      body:'hello@d2slab.org\ncollaborations@d2slab.org' },
                { icon:'🕐', title:'Office Hours',body:'Monday – Friday\n9:00 AM – 5:00 PM EST\nResponse within 48 hours' },
              ].map(({ icon, title, body }) => (
                <div key={title} className="glass-card reveal p-7 flex gap-5">
                  <span className="text-2xl flex-shrink-0 mt-0.5">{icon}</span>
                  <div>
                    <h4 className="font-display font-bold text-sm tracking-[-0.01em] mb-2">{title}</h4>
                    <p className="text-sm font-light leading-relaxed whitespace-pre-line" style={{color:'var(--text-2)'}}>{body}</p>
                  </div>
                </div>
              ))}

              {/* Social */}
              <div className="glass-card reveal p-7">
                <h4 className="font-display font-bold text-sm tracking-[-0.01em] mb-5">Follow Our Research</h4>
                <div className="flex flex-col gap-3">
                  {[
                    { name:'Google Scholar', url:'#', desc:'50+ publications' },
                    { name:'ResearchGate',   url:'#', desc:'Connect with the team' },
                    { name:'GitHub',         url:'#', desc:'Open-source tools' },
                    { name:'LinkedIn',       url:'#', desc:'Lab updates & news' },
                  ].map(({ name, url, desc }) => (
                    <a key={name} href={url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-xl border transition-all duration-300 group"
                      style={{background:'rgba(255,255,255,0.02)',borderColor:'var(--border)'}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(0,255,255,0.25)';e.currentTarget.style.background='rgba(0,255,255,0.03)'}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='rgba(255,255,255,0.02)'}}>
                      <div>
                        <p className="font-display font-semibold text-sm">{name}</p>
                        <p className="text-xs font-light" style={{color:'var(--text-3)'}}>{desc}</p>
                      </div>
                      <svg className="w-4 h-4" style={{color:'rgba(0,255,255,0.4)'}} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                      </svg>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Form column */}
            <div className="lg:col-span-3">
              <div className="glass-card reveal" style={{padding:'48px 44px',position:'relative'}}>
                <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-[18px]"
                  style={{background:'linear-gradient(90deg,transparent,#00ffff,#8b5cf6,transparent)'}} />

                {submitted ? (
                  <div className="text-center py-16">
                    <div className="text-6xl mb-6">✅</div>
                    <h3 className="font-display font-bold text-2xl tracking-[-0.04em] mb-3">Message Sent!</h3>
                    <p className="text-sm font-light leading-relaxed mb-8 max-w-sm mx-auto" style={{color:'var(--text-2)'}}>
                      Thanks for reaching out. We'll get back to you within 48 hours.
                    </p>
                    <button className="btn btn--glass" onClick={() => { setSubmit(false); setForm({ name:'', email:'', org:'', reason:'collaboration', message:'' }); }}>
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-display font-bold text-[1.5rem] tracking-[-0.04em] mb-2">Send Us a Message</h3>
                    <p className="text-sm font-light mb-8" style={{color:'var(--text-2)'}}>We read every message and respond within 48 hours.</p>

                    <form onSubmit={handleSubmit}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                        <div>
                          <label style={labelStyle}>Your Name *</label>
                          <input name="name" required value={form.name} onChange={handleChange} placeholder="Dr. Jane Smith"
                            style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                        </div>
                        <div>
                          <label style={labelStyle}>Email Address *</label>
                          <input name="email" type="email" required value={form.email} onChange={handleChange} placeholder="jane@university.edu"
                            style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                        </div>
                      </div>

                      <div className="mb-5">
                        <label style={labelStyle}>Organization / Institution</label>
                        <input name="org" value={form.org} onChange={handleChange} placeholder="MIT, Argonne National Lab, etc."
                          style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
                      </div>

                      <div className="mb-5">
                        <label style={labelStyle}>Reason for Contact *</label>
                        <select name="reason" value={form.reason} onChange={handleChange} style={{...inputStyle,cursor:'pointer',appearance:'none'}}>
                          {REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </div>

                      <div className="mb-8">
                        <label style={labelStyle}>Message *</label>
                        <textarea name="message" required rows={5} value={form.message} onChange={handleChange}
                          placeholder="Tell us about your research interests, collaboration ideas, or any questions you have..."
                          style={{...inputStyle,resize:'vertical',minHeight:140}} onFocus={onFocus} onBlur={onBlur} />
                      </div>

                      <button type="submit" disabled={loading}
                        className="btn btn--primary w-full justify-center"
                        style={{opacity:loading?.6:1,cursor:loading?'wait':'pointer'}}>
                        {loading ? (
                          <><span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />Sending...</>
                        ) : 'Send Message'}
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
